import { useState } from 'react';
import { ChevronRight, ExternalLink, Table2 } from 'lucide-react';
import type { DataResultBlock } from '@netsuite-ai/types';

interface Props {
  block: DataResultBlock;
}

/**
 * Fields the API sends, or the design needs, that `DataResultBlock` does not
 * declare yet. Read through one cast so this file compiles against today's
 * types — fold them into `@netsuite-ai/types` and delete this.
 */
interface DataResultExtras {
  /** Agent-authored description — "Aged receivables — North · 60+ days". */
  title?: string;
  /** Query wall time, rendered as "0.42s". */
  elapsedMs?: number;
  /** Provenance line — "Saved Search: Aging — North". */
  source?: string;
  /** Deep link into the NetSuite UI for this result. */
  recordUrl?: string;
  /** Opaque page token. Its presence IS the "more rows exist" signal. */
  cursor?: string;
}

/**
 * SuiteQL rows arrive as positional arrays aligned to `columns`
 * (`["IN-2912573", 45843240, "7/24/2026", "Invoice : Open"]`), but blocks
 * persisted earlier hold keyed objects. Every read goes through `cellAt`, so
 * both shapes render — remove the object branch once the old blocks age out.
 */
type ResultRow = unknown[] | Record<string, unknown>;

const VISIBLE_ROWS = 5;

/** The table is a preview, not the full grid — Export CSV carries every column. */
const MAX_COLS = 5;

// Plumbing the user can't act on. Still selected (the agent needs id for
// follow-up actions) and still exported, just not shown.
const HIDDEN_COLS = new Set(['id', 'internalid']);

// The agent authors column names and aliases freely (`AS "PO Number"`), so every
// name comparison normalises to letters-only once here, not at each call site.
const norm = (c: string) => c.toLowerCase().replace(/[^a-z]/g, '');
const labelize = (c: string) => c.replace(/_/g, ' ');

function formatValue(v: unknown): string {
  if (v === null || v === undefined || v === '') return '';
  if (typeof v === 'number')
    return v.toLocaleString(undefined, { maximumFractionDigits: 2 });
  return String(v);
}

/** Positional rows are read by index, keyed rows by name. */
function cellAt(row: ResultRow, index: number, name: string): unknown {
  return Array.isArray(row) ? row[index] : row[name];
}

/**
 * RFC 4180 escaping, plus two things the spec doesn't cover: spreadsheet
 * formula injection, and the reference objects SuiteQL joins hand back.
 */
function csvCell(v: unknown): string {
  if (v === null || v === undefined) return '';

  // Numbers and booleans are never injection vectors, and the sigil guard
  // below would wreck a negative amount.
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);

  const raw =
    typeof v === 'object'
      ? ((v as { refName?: string }).refName ?? JSON.stringify(v))
      : String(v);

  // Excel and Sheets evaluate a cell that opens with a formula sigil, and
  // memo/description fields are user-authored. A leading apostrophe is the
  // spreadsheet convention for "the rest of this is literally text".
  const s = /^[=+\-@\t\r]/.test(raw) ? `'${raw}` : raw;

  return /[",\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

function downloadCsv(filename: string, csv: string) {
  // BOM so Excel reads it as UTF-8 rather than the local codepage.
  const url = URL.createObjectURL(
    new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8' }),
  );
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.style.display = 'none';
  // Firefox won't act on a click targeting a detached anchor.
  document.body.appendChild(a);
  a.click();
  a.remove();
  // The click only queues the fetch. Revoking on this tick can cancel it.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function slugify(s: string): string {
  return (
    s
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '') || 'data-result'
  );
}

export function DataResult({ block }: Props) {
  const [showAll, setShowAll] = useState(false);
  const [showQuery, setShowQuery] = useState(false);

  const { columns, totalCount, hasMore } = block;
  const rows = block.rows as unknown as ResultRow[];
  const { title, elapsedMs, source, recordUrl, cursor } =
    block as DataResultBlock & DataResultExtras;

  // Empty results are no longer emitted, but conversations persisted before that
  // change still hold them — render nothing rather than an empty card.
  if (rows.length === 0) return null;

  // Each column keeps its ORIGINAL index, which is how a positional row is read.
  // Filtering and capping must not disturb it.
  const shown = columns
    .map((name, index) => ({ name, index }))
    .filter((c) => !HIDDEN_COLS.has(norm(c.name)))
    .slice(0, MAX_COLS);

  const visibleRows = showAll ? rows : rows.slice(0, VISIBLE_ROWS);
  const hiddenCount = rows.length - visibleRows.length;

  const numericCols = new Set(
    shown
      .filter((c) =>
        rows.some((r) => typeof cellAt(r, c.index, c.name) === 'number'),
      )
      .map((c) => c.index),
  );

  // `totalCount` is this page's row count, so "N results" is only true of an
  // unpaged query — once there's a cursor, the honest claim is about the page.
  const paged = hasMore ?? Boolean(cursor);
  const count = totalCount ?? rows.length;
  const meta = [
    paged ? `first ${count} rows` : `${count} result${count === 1 ? '' : 's'}`,
    elapsedMs === undefined ? null : `${(elapsedMs / 1000).toFixed(2)}s`,
  ].filter(Boolean);

  // Every column, including the ones the table caps off and the id it hides.
  // Raw values, not `formatValue`: a thousands separator would split the cell
  // and a rounded number is not what a spreadsheet should receive. Note this
  // is the page that was fetched — a paged query has more rows behind it.
  function exportCsv() {
    const csv = [
      columns.map(csvCell).join(','),
      ...rows.map((r) =>
        columns.map((name, i) => csvCell(cellAt(r, i, name))).join(','),
      ),
    ].join('\r\n');
    downloadCsv(`${slugify(title ?? 'data result')}.csv`, csv);
  }

  return (
    <div className="my-2 w-full overflow-hidden rounded border border-neutral-200 bg-white shadow-sm">
      <div className="flex items-start gap-2 border-b border-neutral-100 px-4 py-3">
        <Table2 size={16} className="text-primary mt-0.5 shrink-0" />
        <p className="min-w-0 flex-1 truncate font-semibold text-neutral-900">
          {title ?? 'Data result'}
        </p>
        <p className="shrink-0 text-xs whitespace-nowrap text-neutral-500">
          {meta.join(' · ')}
        </p>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-neutral-100 bg-neutral-50">
              {shown.map((col) => (
                <th
                  key={col.index}
                  className={`px-4 py-2 text-xs font-medium whitespace-nowrap text-neutral-500 first-letter:uppercase ${
                    numericCols.has(col.index) ? 'text-right' : 'text-left'
                  }`}
                >
                  {labelize(col.name)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-neutral-100">
            {visibleRows.map((row, i) => (
              <tr key={i}>
                {shown.map((col) => (
                  <td
                    key={col.index}
                    className={`px-4 py-2 whitespace-nowrap text-neutral-700 ${
                      numericCols.has(col.index)
                        ? 'text-right tabular-nums'
                        : 'text-left'
                    }`}
                  >
                    {formatValue(cellAt(row, col.index, col.name))}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {!showAll && hiddenCount > 0 && (
        <button
          onClick={() => setShowAll(true)}
          className="text-primary w-full border-t border-neutral-100 bg-neutral-50 py-2.5 text-center text-sm font-medium hover:bg-neutral-100"
        >
          Show {hiddenCount} more
        </button>
      )}

      {/* The header meta already says the count is a page, but not that more
          rows exist behind it — that claim only belongs here. */}
      {paged && (
        <p className="border-t border-neutral-100 px-4 py-2 text-xs text-neutral-500">
          More rows match this query.
        </p>
      )}

      <div className="border-t border-neutral-100">
        <button
          onClick={() => setShowQuery((v) => !v)}
          className="flex w-full items-center gap-2 px-4 py-2.5 text-sm font-medium text-neutral-700 hover:bg-neutral-50"
        >
          <ChevronRight
            size={18}
            className={`transition-transform duration-150 ${showQuery ? 'rotate-90' : ''}`}
          />
          Show query
        </button>

        {showQuery && (
          <div className="border-t border-neutral-100 bg-neutral-50 px-4 py-3">
            <code className="block text-xs whitespace-pre-wrap text-neutral-600">
              {block.query ?? columns.join(', ')}
            </code>
          </div>
        )}
      </div>

      <div className="flex items-center gap-3 border-t border-neutral-100 bg-neutral-50 px-4 py-3">
        {source && (
          <p className="min-w-0 flex-1 truncate text-xs text-neutral-500">
            Source ·{' '}
            <span className="font-medium text-neutral-700">{source}</span>
          </p>
        )}
        <div className="ml-auto flex shrink-0 items-center gap-2">
          <button
            onClick={exportCsv}
            className="rounded border border-neutral-200 bg-white px-3 py-1.5 text-xs font-medium text-neutral-700 hover:bg-neutral-50"
          >
            {paged ? 'Export page CSV' : 'Export CSV'}
          </button>

          {recordUrl && (
            <a
              href={recordUrl}
              target="_blank"
              rel="noreferrer"
              className="flex items-center gap-1.5 rounded border border-neutral-200 bg-white px-3 py-1.5 text-xs font-medium text-neutral-700 hover:bg-neutral-50"
            >
              Open in NetSuite
              <ExternalLink size={12} className="text-neutral-400" />
            </a>
          )}
        </div>
      </div>
    </div>
  );
}
